import {
  AloftArea,
  AloftDate,
  Improvements,
  ORDER_FEATURES,
  isDefined,
  orUndefined,
  schAloftArea,
} from '@aloft/toolkit-shared';

// Difference thresholds for unexpectedCompAdjustment. This alert asks whether the Subject and Comp
// are close enough that no adjustment is expected, which is a narrower question than whether a
// difference is worth adjusting for. It deliberately does not share thresholds with the
// missingCompAdjustment materiality helpers in compAdjustmentMateriality.ts.
export const SITE_SIZE_DIFFERENCE_THRESHOLD = 0.05;
export const GLA_DIFFERENCE_THRESHOLD_SQFT = 100;
export const BASEMENT_AREA_DIFFERENCE_THRESHOLD = 0.15;
export const HALF_BATH_DIFFERENCE_THRESHOLD = 2;

const PARKING_TYPES = ['garage', 'detachedGarage', 'carport'] as const;
type ParkingType = (typeof PARKING_TYPES)[number];

export type BathCounts = { fullBaths?: number; halfBaths?: number };

const toSqft = (area?: AloftArea): number => area?.toSqFt().unitAmount ?? 0;

const bothAreasPresent = (subject?: AloftArea, comp?: AloftArea): boolean =>
  isDefined(subject) &&
  isDefined(comp) &&
  !subject.equals(AloftArea.zero) &&
  !comp.equals(AloftArea.zero);

// Comp sale dates are only ever recorded to month precision, so the Effective Date is compared at
// the same precision.
export const hasSaleDateDifference = (effectiveDate: AloftDate, compSaleMonth: string): boolean =>
  compSaleMonth !== effectiveDate.format('yyyy-MM');

export const hasSiteSizeDifference = (subjectSite?: AloftArea, compSite?: AloftArea): boolean =>
  bothAreasPresent(subjectSite, compSite) &&
  Math.abs(toSqft(compSite) - toSqft(subjectSite)) / toSqft(subjectSite) >
    SITE_SIZE_DIFFERENCE_THRESHOLD;

export const hasGlaDifference = (subjectGla?: AloftArea, compGla?: AloftArea): boolean =>
  bothAreasPresent(subjectGla, compGla) &&
  Math.abs(toSqft(compGla) - toSqft(subjectGla)) > GLA_DIFFERENCE_THRESHOLD_SQFT;

export const hasRatingDifference = (subjectRating: unknown, compRating: unknown): boolean =>
  isDefined(subjectRating) && isDefined(compRating) && subjectRating !== compRating;

export const hasBathDifference = (subject: BathCounts, comp: BathCounts): boolean => {
  if (
    isDefined(subject.fullBaths) &&
    isDefined(comp.fullBaths) &&
    subject.fullBaths !== comp.fullBaths
  ) {
    return true;
  }

  if (!isDefined(subject.halfBaths) || !isDefined(comp.halfBaths)) return false;
  return Math.abs(comp.halfBaths - subject.halfBaths) >= HALF_BATH_DIFFERENCE_THRESHOLD;
};

// Every parking type is compared separately so properties with more than one type are fully covered.
// An absent type yields undefined, which never matches a present type's space count.
const parkingSpaces = (
  improvements: Improvements | undefined,
  type: ParkingType,
): number | undefined =>
  ORDER_FEATURES[type].hasFeature({ [type]: improvements?.[type] } as Improvements)
    ? (improvements?.[type]?.spaces ?? 0)
    : undefined;

export const hasGarageCarportDifference = (subject?: Improvements, comp?: Improvements): boolean =>
  PARKING_TYPES.some((type) => parkingSpaces(subject, type) !== parkingSpaces(comp, type));

const basementAreas = (improvements?: Improvements) => ({
  hasFeature: ORDER_FEATURES.basement.hasFeature({
    basement: improvements?.basement,
  } as Improvements),
  totalArea: orUndefined(schAloftArea.parse)(improvements?.basement?.totalArea),
  finishedArea: orUndefined(schAloftArea.parse)(improvements?.basement?.finishedArea),
});

// An area present on one side only is a finish level change, which counts as a difference whatever
// its size.
const basementAreaDiffers = (subject?: AloftArea, comp?: AloftArea): boolean => {
  const subjectSqft = toSqft(subject);
  const compSqft = toSqft(comp);
  if (subjectSqft === 0 && compSqft === 0) return false;
  if (subjectSqft === 0 || compSqft === 0) return true;
  return Math.abs(compSqft - subjectSqft) / subjectSqft > BASEMENT_AREA_DIFFERENCE_THRESHOLD;
};

export const hasBasementDifference = (subject?: Improvements, comp?: Improvements): boolean => {
  const subjectBasement = basementAreas(subject);
  const compBasement = basementAreas(comp);

  if (subjectBasement.hasFeature !== compBasement.hasFeature) return true;
  if (!subjectBasement.hasFeature) return false;

  return (
    basementAreaDiffers(subjectBasement.totalArea, compBasement.totalArea) ||
    basementAreaDiffers(subjectBasement.finishedArea, compBasement.finishedArea)
  );
};
